import { PriceCandle, SupportedResolution } from '@ui/services/pyth-benchmark/types';

type PythProHistoryResponse =
  | {
      s: 'ok';
      t: number[];
      o: number[];
      h: number[];
      l: number[];
      c: number[];
      v?: number[];
    }
  | {
      s: 'error';
      errorMessage?: string;
    };

export type GetPythProHistoryParams = {
  /**
   * Pyth Pro History API accepts full TradingView symbols, for example Crypto.BTC/USD.
   */
  symbol: string;
  /**
   * Used as the availability gate and cache discriminator. The /history
   * endpoint itself resolves candles by symbol.
   */
  feedId: string;
  resolution: SupportedResolution;
  from: number;
  to: number;
};

const PYTH_PRO_HISTORY_ENDPOINT =
  'https://pyth.dourolabs.app/v1/fixed_rate@200ms/history';

const toPythProResolution = (resolution: SupportedResolution) => {
  if (resolution === '1D') return 'D';
  if (resolution === '1W') return 'W';

  return resolution;
};

const toCandle = (data: Extract<PythProHistoryResponse, { s: 'ok' }>) => {
  return data.t.map((timestamp, index): PriceCandle => {
    return {
      time: timestamp * 1000,
      open: data.o[index],
      high: data.h[index],
      low: data.l[index],
      close: data.c[index],
    };
  });
};

export const getPythProHistory = async ({
  from,
  resolution,
  symbol,
  to,
}: GetPythProHistoryParams): Promise<PriceCandle[]> => {
  const searchParams = new URLSearchParams({
    symbol,
    resolution: toPythProResolution(resolution),
    from: String(from),
    to: String(to),
  });
  const res = await fetch(
    `${PYTH_PRO_HISTORY_ENDPOINT}?${searchParams.toString()}`,
  );
  const response = (await res.json()) as PythProHistoryResponse;

  if (response.s === 'error') {
    throw new Error(response.errorMessage ?? 'Failed to fetch Pyth Pro history');
  }

  return toCandle(response);
};
